import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { LiveCallEngine, CallBar, blobToBase64, micErrorText } from './LiveCall';

/**
 * 🎙 BOSHQARUVCHI — Khorezmai'ni suhbat orqali boshqarish (F1, 2026-09-27).
 * Backend: routes/conductor.js + services/conductor.js (docs/knowledge/146).
 *
 * variant='navbar' — tepadagi qatorda tugma (⚡ 🩺 👤 ⏳ bilan bir xil), bosilsa o'ngda oyna.
 * variant='mobile' — /m telefon panelida suzuvchi tugma + to'liq ekran.
 * Jonli oqim (matn, tool qadamlari, tasdiq kartalari) o'z socket'i orqali — faqat oyna
 * ochiq paytda ulanadi. `authFetch` har render'da yangi — ref orqali (effekt dep'iga emas).
 * Qaytarib bo'lmaydigan amal = karta + ✅ tugma: amal FAQAT shu tugma bosilganda bajariladi.
 */

const TOOL_ST = { running: '⏳', ok: '✓', error: '✗' };
const CONF_ST = {
  pending: { t: 'Tasdiq kutilmoqda', c: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' },
  running: { t: '⏳ Bajarilmoqda…', c: 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20' },
  done: { t: '✅ Bajarildi', c: 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20' },
  failed: { t: '❌ Xato', c: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20' },
  rejected: { t: '✖ Bekor qilindi', c: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800' },
  expired: { t: '⌛ Muddati o\'tdi', c: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800' },
};
const EXAMPLES = [
  'Mendan nima kutilyapti?',
  'Oxirgi yiqilgan vazifalar va sababi',
  'TezHal loyihasining holati qanday?',
  'Test muhitlarida muammo bormi?',
];
// 👥 F5: mijoz (studiya operatori emas) uchun — texnik bo'lmagan misollar.
const CLIENT_EXAMPLES = [
  'Loyihamda nima o\'zgardi?',
  'Saytim hozir qanday ko\'rinadi? Rasmini ko\'rsating',
  'Yangi funksiya qo\'shmoqchiman',
  'Bu ish qachon tayyor bo\'ladi?',
];
const fmtUsd = (n) => `$${(Number(n) || 0).toFixed((Number(n) || 0) < 1 ? 3 : 2)}`;
const ts = (v) => { const d = new Date(v); return isNaN(d) ? 0 : d.getTime(); };

// Oddiy formatlash: **qalin**, `kod`, havola, «- » ro'yxat. HTML'siz (XSS yo'q).
function Inline({ text }) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|`[^`]+`|https?:\/\/[^\s)]+)/g);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) return <b key={i}>{p.slice(2, -2)}</b>;
    if (p.startsWith('`') && p.endsWith('`') && p.length > 2) return <code key={i} className="px-1 rounded bg-gray-100 dark:bg-gray-700 text-[12px]">{p.slice(1, -1)}</code>;
    if (/^https?:\/\//.test(p)) return <a key={i} href={p} target="_blank" rel="noreferrer" className="text-primary-600 dark:text-primary-400 underline break-all">{p}</a>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}
function Rich({ text }) {
  const lines = String(text || '').split('\n');
  return (
    <div className="space-y-1">
      {lines.map((ln, i) => {
        const m = ln.match(/^\s*[-•*]\s+(.*)$/);
        if (m) return <div key={i} className="flex gap-1.5 pl-1"><span className="text-gray-400">•</span><span><Inline text={m[1]} /></span></div>;
        if (!ln.trim()) return <div key={i} className="h-1" />;
        return <div key={i}><Inline text={ln} /></div>;
      })}
    </div>
  );
}

// 🖼 F5: agent ko'rsatgan sahifa rasmi / sinov rasmlari va videolari (fayllar API'da /media, /qa-videos).
function Media({ apiUrl, media }) {
  if (!Array.isArray(media) || !media.length) return null;
  const src = (u) => (/^https?:\/\//.test(u) ? u : `${apiUrl || ''}${u}`);
  return (
    <div className="flex flex-wrap gap-2 pl-1">
      {media.map((m, i) => m.type === 'video' ? (
        <video key={i} src={src(m.url)} controls preload="metadata" className="max-w-full w-72 rounded-lg border border-gray-200 dark:border-gray-700" />
      ) : (
        <a key={i} href={src(m.url)} target="_blank" rel="noreferrer" title={m.caption || 'Kattalashtirish'} className="block">
          <img src={src(m.url)} alt={m.caption || 'rasm'} loading="lazy" className="max-h-56 max-w-full rounded-lg border border-gray-200 dark:border-gray-700 hover:opacity-90" />
        </a>
      ))}
    </div>
  );
}

// ── 🗂 B2: suhbat lentasidagi JONLI vazifa kartasi ─────────────────────────────────────────
// Qoidalar ⏳ Jarayonlar (App.js ProcessesPanel) bilan BIR XIL: faol → ⏹, yiqilgan (eskirmagan) → ▶,
// hamma qism tugagan va kamida bittasi bajarilgan → ✅ Tasdiqlash. Savol / reja kutayotgan bo'lsa —
// javob 📋 Vazifa tabida beriladi (u yerda to'liq forma bor).
const T_ACTIVE = ['pending', 'pending_architecture', 'in_progress', 'awaiting_clarification', 'awaiting_plan_approval', 'awaiting_owner_approval'];
const T_LABEL = {
  pending_architecture: '🧠 Rejalashtirilmoqda', pending: '⏳ Kutilmoqda', completed: '✅ Bajarildi', in_progress: '🔄 Jarayonda',
  failed: '❌ Xatolik', cancelled: "⏹ To'xtatilgan", awaiting_clarification: '❓ Aniqlik kerak',
  awaiting_plan_approval: '📝 Reja tasdiq kutmoqda', awaiting_owner_approval: '🙋 Ruxsat kutmoqda',
};
const T_COLOR = {
  pending_architecture: 'bg-purple-200 dark:bg-purple-800/60 text-purple-900 dark:text-purple-200',
  pending: 'bg-yellow-200 dark:bg-yellow-800/60 text-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-200 dark:bg-green-800/60 text-green-900 dark:text-green-200',
  in_progress: 'bg-blue-200 dark:bg-blue-800/60 text-blue-900 dark:text-blue-200',
  failed: 'bg-red-200 dark:bg-red-800/60 text-red-900 dark:text-red-200',
  cancelled: 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300',
  awaiting_clarification: 'bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-200',
  awaiting_plan_approval: 'bg-indigo-200 dark:bg-indigo-800/60 text-indigo-900 dark:text-indigo-200',
  awaiting_owner_approval: 'bg-violet-200 dark:bg-violet-800/60 text-violet-900 dark:text-violet-200',
};
const T_DEPLOY = {
  merge: '🔀 Merge qilinmoqda', deploy: '🚀 Deploy qilinmoqda', apk_build: '🔨 APK qurilmoqda', apk_install: '📲 Telefonga o‘rnatilmoqda',
  prod: '🚀 Production deploy', installed: '📲 Ilova o‘rnatildi', test_only: '🧪 Faqat test env', partial: '⚠️ Qisman deploy', interrupted: '⚠️ Deploy uzildi',
};
const SUB_ICON = { completed: '✅', in_progress: '🔄', pending: '⏳', failed: '❌', cancelled: '⏹' };
const taskActive = (t) => T_ACTIVE.includes(t.status) || (t.sub_tasks || []).some(s => T_ACTIVE.includes(s.status));
const taskSuperseded = (st, all) => all.some(o => o.id > st.id && o.module_id === st.module_id
  && o.task_type === st.task_type && !['failed', 'cancelled'].includes(o.status));
const taskCanResume = (t) => !t.approved_at && (['failed', 'cancelled'].includes(t.status)
  || (t.sub_tasks || []).some(st => ['failed', 'cancelled'].includes(st.status) && !taskSuperseded(st, t.sub_tasks)));
const taskCanApprove = (t) => !t.approved_at && (t.sub_tasks || []).length > 0
  && t.sub_tasks.every(st => ['completed', 'failed', 'cancelled'].includes(st.status))
  && t.sub_tasks.some(st => st.status === 'completed');

function TaskLiveCard({ task, busy, approving, onStop, onResume, onApprove, onOpen }) {
  if (!task) return <div className="rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 text-xs text-gray-400">⏳ Vazifa yuklanmoqda…</div>;
  if (task.missing) return null;   // o'chirilgan yoki ko'rish huquqi yo'q
  const subs = task.sub_tasks || [];
  const done = subs.filter(s => s.status === 'completed').length;
  const needsYou = ['awaiting_clarification', 'awaiting_plan_approval'].includes(task.status);
  const btn = 'px-2 py-1 text-xs font-semibold rounded-lg disabled:opacity-50 transition whitespace-nowrap';
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 space-y-1.5 shadow-sm">
      <div className="flex items-center gap-2 min-w-0">
        <button type="button" onClick={onOpen} title="📋 Vazifa tabida ochish"
          className="text-xs font-semibold text-primary-700 dark:text-primary-400 hover:underline shrink-0">#{task.id}</button>
        <span className="text-[11px] text-gray-400 truncate" translate="no">{task.project_name}</span>
        <span className={`ml-auto px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${T_COLOR[task.status] || 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'} ${T_ACTIVE.includes(task.status) ? 'animate-pulse' : ''}`}>
          {T_LABEL[task.status] || task.status}
        </span>
      </div>
      <div className="text-[13px] text-gray-800 dark:text-gray-200 break-words" translate="no" title={task.instruction}
        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{task.instruction}</div>
      {subs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {subs.map(s => (
            <span key={s.id} title={`#${s.id} · ${s.task_type || ''} · ${T_LABEL[s.status] || s.status}`}
              className={`text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 ${s.status === 'in_progress' ? 'animate-pulse' : ''}`}>
              {SUB_ICON[s.status] || '•'} <span translate="no">{s.module_name || s.module_type || '—'}</span>{s.task_type && s.task_type !== 'developer' ? ` · ${s.task_type}` : ''}
            </span>
          ))}
          <span className="text-[10px] text-gray-400">{done}/{subs.length}</span>
        </div>
      )}
      {(task.approved_at || T_DEPLOY[task.deploy_stage]) && (
        <div className="text-[11px] text-gray-500 dark:text-gray-400">
          {T_DEPLOY[task.deploy_stage] || ''}{task.approved_at ? `${T_DEPLOY[task.deploy_stage] ? ' · ' : ''}${task.deploy_stage === 'test_only' ? '✅ Tasdiqlandi' : '🚀 Deploy qilindi'}` : ''}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {taskActive(task) && (
          <button type="button" onClick={onStop} disabled={busy}
            className={`${btn} bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/70`}>⏹ To'xtatish</button>
        )}
        {needsYou && (
          <button type="button" onClick={onOpen}
            className={`${btn} bg-amber-500 text-white hover:bg-amber-600`}>{task.status === 'awaiting_plan_approval' ? '📝 Rejani ko‘rish' : '❓ Javob berish'}</button>
        )}
        {taskCanResume(task) && (
          <button type="button" onClick={onResume} disabled={busy} title="Yiqilgan qism vazifalarni qayta navbatga qo'yish (yarim qilingan ish saqlanadi)"
            className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>▶ Davom ettirish</button>
        )}
        {taskCanApprove(task) && (
          <button type="button" onClick={onApprove} disabled={approving} title={`Review branch (kh/task-${task.id}) ni asosiy branchga merge qilib, test-muhitni qayta deploy qiladi`}
            className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>{approving ? '⏳ Merge…' : '✅ Tasdiqlash'}</button>
        )}
      </div>
    </div>
  );
}

// embedded — web «Jarayonlar» panelining 💬 Suhbat tabi ichida (o'z ramkasi/soyasi yo'q).
// projectId — ekranda tanlangan loyiha: har xabar bilan yuboriladi, server a'zolikni tekshirib
// promptga «[EKRANDA TANLANGAN LOYIHA]» qilib qo'yadi (loyiha aytilmasa shu nazarda tutiladi).
// active — tab ko'rinib turibdimi (yashirin bo'lsa vazifa kartalari poll qilinmaydi); onOpenTask — 📋 Vazifa tabiga;
// topSlot — sarlavha ostidagi qator («Hozir bajarilmoqda»).
export function ConductorChat({ authFetch, apiUrl, token, channel, onClose, fullScreen, onUiAction, embedded = false, projectId = null,
  active = true, onOpenTask = null, topSlot = null }) {
  const afRef = useRef(authFetch); afRef.current = authFetch;
  const projRef = useRef(projectId); projRef.current = projectId;
  // 🗂 B2: lentadagi jonli vazifa kartalari — {taskId: task}; har vazifa faqat OXIRGI eslatilgan joyida.
  const [cards, setCards] = useState({});
  const [cardBusy, setCardBusy] = useState({});
  const [approvingCard, setApprovingCard] = useState(null);   // {id, at}
  const cardKeyRef = useRef('');
  // 🧭 Tool so'ragan UI amali (masalan konsol sahifasini ochish) — App bajaradi.
  const uiRef = useRef(onUiAction); uiRef.current = onUiAction;
  const [me, setMe] = useState(null);
  const [threads, setThreads] = useState([]);
  const [showThreads, setShowThreads] = useState(false);
  const [threadId, setThreadId] = useState(() => { try { return Number(localStorage.getItem('kh_conductor_thread')) || null; } catch (e) { return null; } });
  const [view, setView] = useState({ items: [], confirmations: [] });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [live, setLive] = useState({ text: '', tools: [], media: [] });
  const [deciding, setDeciding] = useState(null);
  // 🎤 F3: ovoz. speakOn — javobni ovozda o'qish (pullik, o'chirsa bo'ladi), eslab qolinadi.
  const [speakOn, setSpeakOnRaw] = useState(() => { try { return localStorage.getItem('kh_conductor_speak') !== '0'; } catch (e) { return true; } });
  const setSpeakOn = (v) => { setSpeakOnRaw(v); try { localStorage.setItem('kh_conductor_speak', v ? '1' : '0'); } catch (e) { } if (!v) stopAudio(); };
  const [rec, setRec] = useState(null);          // { secs } yozilayotganda
  const [voiceBusy, setVoiceBusy] = useState(false);   // 🎤 navbati ketmoqda — 🎤 bosilsa bo'linadi
  const voiceBusyRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  // 📡 F4: Navbatchi holatlari (oyna ichida alohida ko'rinish)
  const [showOv, setShowOv] = useState(false);
  const [ov, setOv] = useState(null);            // [] | null
  const [ovBusy, setOvBusy] = useState(null);
  // 📞 F6: jonli qo'ng'iroq (tugmasiz uzluksiz suhbat) — services/LiveCall.js dvigateli.
  const [call, setCall] = useState(null);          // null | { state, level, secs, muted }
  const engineRef = useRef(null);
  const callChainRef = useRef(Promise.resolve());
  const voiceSeqRef = useRef(0);
  const callBusyRef = useRef(false);
  const callTimerRef = useRef(null);
  const recRef = useRef(null);                    // { mr, stream, chunks, timer, cancel }
  const audioRef = useRef({ ctx: null, head: 0, chain: Promise.resolve(), sources: [] });
  const speakRef = useRef(speakOn); speakRef.current = speakOn;
  const tidRef = useRef(threadId); tidRef.current = threadId;
  const sendingRef = useRef(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const rememberThread = (id) => { setThreadId(id); try { if (id) localStorage.setItem('kh_conductor_thread', String(id)); else localStorage.removeItem('kh_conductor_thread'); } catch (e) { } };

  const loadMe = async () => {
    try { const r = await afRef.current(`${apiUrl}/conductor/me`); if (r.ok) setMe(await r.json()); } catch (e) { }
  };
  const loadThreads = async () => {
    try { const r = await afRef.current(`${apiUrl}/conductor/threads`); if (r.ok) setThreads(await r.json()); } catch (e) { }
  };
  const loadThread = async (id) => {
    if (!id) { setView({ items: [], confirmations: [] }); return; }
    try {
      const r = await afRef.current(`${apiUrl}/conductor/threads/${id}`);
      if (r.status === 404) { rememberThread(null); setView({ items: [], confirmations: [] }); return; }
      if (r.ok) setView(await r.json());
    } catch (e) { }
  };

  const loadOv = async () => {
    try { const r = await afRef.current(`${apiUrl}/overseer/items`); if (r.ok) setOv(await r.json()); } catch (e) { }
  };
  const ovAct = async (id, action) => {
    setOvBusy(id);
    try {
      const r = await afRef.current(`${apiUrl}/overseer/items/${id}/${action}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) toast.error(d.error || 'Bajarib bo\'lmadi'); else toast.success(action === 'fix' ? (d.result || 'Tuzatildi') : 'Belgilandi');
      await loadOv();
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setOvBusy(null); }
  };
  const briefingNow = async () => {
    setOvBusy('brief');
    try {
      const r = await afRef.current(`${apiUrl}/overseer/briefing`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { toast.error(d.error || d.skipped || 'Hisobot tayyorlanmadi'); return; }
      toast.success(d.telegram ? 'Hisobot tayyor — Telegram\'ga ham yuborildi' : 'Hisobot tayyor');
      rememberThread(d.threadId); await loadThread(d.threadId); loadThreads(); setShowOv(false);
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setOvBusy(null); }
  };

  useEffect(() => { loadOv(); loadMe(); loadThreads(); loadThread(threadId); setTimeout(() => inputRef.current && inputRef.current.focus(), 50); /* eslint-disable-next-line */ }, []);

  // Jonli voqealar — faqat oyna ochiq paytda.
  useEffect(() => {
    if (!token) return undefined;
    const s = io(apiUrl || '', { auth: { token } });
    const mine = (d) => d && (d.threadId === tidRef.current || (sendingRef.current && !tidRef.current));
    const adopt = (d) => { if (!tidRef.current && d && d.threadId) { tidRef.current = d.threadId; rememberThread(d.threadId); } };
    s.on('conductor:delta', (d) => { if (!mine(d)) return; adopt(d); setLive(l => ({ ...l, text: l.text + (d.text || '') })); });
    s.on('conductor:reset', (d) => { if (!mine(d)) return; setLive(l => ({ ...l, text: '' })); });
    s.on('conductor:tool', (d) => {
      if (!mine(d)) return; adopt(d);
      // Tool chaqirilganda oldingi oqim matni (odatda «tekshiraman…») chip ostida qoladi.
      setLive(l => {
        const rest = l.tools.filter(t => t.id !== d.id);
        return { ...l, text: d.status === 'running' ? '' : l.text, tools: [...rest, { id: d.id, label: d.label, status: d.status, error: d.error }] };
      });
    });
    s.on('conductor:ui', (d) => { if (!mine(d) || !d.ui || !uiRef.current) return; try { uiRef.current(d.ui); } catch (e) { } });
    s.on('conductor:media', (d) => { if (!mine(d)) return; setLive(l => ({ ...l, media: [...(l.media || []), ...((d && d.media) || [])] })); });
    // 👥 F5: operator mijoz savoliga javob berdi — shu suhbatga yozildi.
    s.on('conductor:message', (d) => {
      if (!d) return;
      toast('💬 Savolingizga javob keldi', { icon: '🎙' });
      if (d.threadId === tidRef.current) loadThread(d.threadId);
      loadThreads();
    });
    s.on('conductor:confirmation', (d) => { if (!mine(d)) return; setView(v => ({ ...v, confirmations: [...(v.confirmations || []).filter(c => c.id !== d.id), { ...d, created_at: new Date().toISOString() }] })); });
    s.on('conductor:decision', (d) => { if (d && d.threadId === tidRef.current) loadThread(d.threadId); });
    // 🗂 B2: 📋 Vazifa formasidan vazifa berildi — shu suhbat lentasiga karta bo'lib tushdi.
    s.on('conductor:event', (d) => { if (d && d.threadId === tidRef.current) loadThread(d.threadId); });
    s.on('conductor:transcript', (d) => {
      if (!mine(d)) return;
      setView(v => ({ ...v, items: (v.items || []).map(it => it.voicePending ? { ...it, text: d.text, voicePending: false } : it) }));
    });
    s.on('conductor:audio', (d) => {
      if (!mine(d) && !(d && d.threadId === tidRef.current)) return; adopt(d);
      if (!d.audio) return;
      // Qo'ng'iroqda ovoz dvigatel orqali (u o'ynayotganini biladi — bo'lish shunga tayanadi).
      if (engineRef.current) engineRef.current.playMp3(d.audio);
      else if (speakRef.current) playChunk(d.audio);
    });
    return () => { s.disconnect(); };
    // eslint-disable-next-line
  }, [token, apiUrl]);

  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: 'end' }); }, [view, live, sending]);

  // ── 🔊 Ovoz: AudioContext tugma bosilganda ochiladi (iOS avto-o'ynatishni shunda ruxsat beradi);
  // bo'laklar kelish tartibida navbat bilan o'ynaladi.
  const ensureCtx = () => {
    const a = audioRef.current;
    if (!a.ctx) { const C = window.AudioContext || window.webkitAudioContext; if (C) a.ctx = new C(); }
    if (a.ctx && a.ctx.state === 'suspended') a.ctx.resume().catch(() => { });
    return a.ctx;
  };
  const stopAudio = () => {
    const a = audioRef.current;
    a.sources.forEach(src => { try { src.stop(); } catch (e) { } });
    a.sources = []; a.head = 0; a.chain = Promise.resolve(); setPlaying(false);
  };
  const playChunk = (b64) => {
    const a = audioRef.current;
    const ctx = ensureCtx(); if (!ctx) return;
    a.chain = a.chain.then(async () => {
      try {
        const bin = atob(b64); const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        const audio = await new Promise((res, rej) => ctx.decodeAudioData(buf.buffer, res, rej));
        const src = ctx.createBufferSource(); src.buffer = audio; src.connect(ctx.destination);
        const start = Math.max(ctx.currentTime + 0.05, a.head);
        src.start(start); a.head = start + audio.duration; a.sources.push(src); setPlaying(true);
        src.onended = () => { a.sources = a.sources.filter(x => x !== src); if (!a.sources.length) setPlaying(false); };
      } catch (e) { /* buzuq bo'lak — keyingisi o'ynaydi */ }
    });
  };
  useEffect(() => () => { stopAudio(); const r = recRef.current; if (r) { try { r.cancel = true; r.mr.stop(); } catch (e) { } } }, []);   // eslint-disable-line

  const pickMime = () => {
    const M = window.MediaRecorder; if (!M || !M.isTypeSupported) return '';
    return ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'].find(t => M.isTypeSupported(t)) || '';
  };
  const startRec = async () => {
    if (rec) return;
    if (sending) {
      // ✋ Agent o'ylayapti yoki gapiryapti (🎤 navbati) — bosish = gapini bo'lib, sizni tinglash.
      if (!voiceBusyRef.current) return;
      cancelServerSpeech(true);
    }
    ensureCtx(); stopAudio();
    if (!navigator.mediaDevices || !window.MediaRecorder) { toast.error('Bu brauzer ovoz yozishni qo\'llamaydi'); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }); }
    catch (e) { toast.error(micErrorText(e), { duration: 8000 }); return; }
    const mime = pickMime();
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const r = { mr, stream, chunks: [], cancel: false, t0: Date.now(), timer: null };
    recRef.current = r;
    mr.ondataavailable = (e) => { if (e.data && e.data.size) r.chunks.push(e.data); };
    mr.onstop = () => {
      clearInterval(r.timer); stream.getTracks().forEach(t => t.stop()); recRef.current = null; setRec(null);
      if (r.cancel) return;
      const blob = new Blob(r.chunks, { type: mr.mimeType || mime || 'audio/webm' });
      if (Date.now() - r.t0 < 700 || blob.size < 1500) { toast('Juda qisqa — tugmani bosib gapiring, tugagach yana bosing'); return; }
      sendVoice(blob);
    };
    mr.start(250);
    setRec({ secs: 0 });
    r.timer = setInterval(() => {
      const secs = Math.floor((Date.now() - r.t0) / 1000);
      setRec({ secs });
      if (secs >= 60) { try { mr.stop(); } catch (e) { } }
    }, 250);
  };
  const stopRec = (cancel = false) => { const r = recRef.current; if (!r) return; r.cancel = cancel; try { r.mr.stop(); } catch (e) { } };

  const sendVoice = async (blob) => {
    const my = (voiceSeqRef.current = voiceSeqRef.current + 1);
    setSending(true); sendingRef.current = true; voiceBusyRef.current = true; setVoiceBusy(true);
    setLive({ text: '', tools: [], media: [] });
    setView(v => ({ ...v, items: [...(v.items || []), { id: `tmp-v-${Date.now()}`, type: 'user', text: '🎤 …', voicePending: true, at: new Date().toISOString() }] }));
    try {
      const b64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1] || ''); fr.onerror = rej; fr.readAsDataURL(blob); });
      const r = await afRef.current(`${apiUrl}/conductor/voice`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId: tidRef.current, audio: b64, speak: speakRef.current, channel, projectId: projRef.current || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.threadId) rememberThread(d.threadId);
      if (!r.ok) {
        toast.error(d.error || 'Xatolik');
        setView(v => ({ ...v, items: [...(v.items || []).filter(it => !it.voicePending), { id: `err-${Date.now()}`, type: 'system', text: d.error || 'Xatolik', at: new Date().toISOString() }] }));
      }
      if (d.limits) setMe(m => (m ? { ...m, limits: d.limits } : m));
      await loadThread(d.threadId || tidRef.current);
      loadThreads();
    } catch (e) {
      toast.error('Ulanib bo\'lmadi');
    } finally {
      // Bo'lingan eski navbat kech tugasa, yangisining holatini o'chirmasin.
      if (voiceSeqRef.current === my) {
        setSending(false); sendingRef.current = false; voiceBusyRef.current = false; setVoiceBusy(false);
        setLive({ text: '', tools: [], media: [] });
      }
    }
  };

  const send = async (textArg) => {
    const text = String(textArg != null ? textArg : input).trim();
    if (!text || sending) return;
    setSending(true); sendingRef.current = true;
    setInput('');
    setLive({ text: '', tools: [], media: [] });
    setView(v => ({ ...v, items: [...(v.items || []), { id: `tmp-${Date.now()}`, type: 'user', text, at: new Date().toISOString() }] }));
    try {
      const r = await afRef.current(`${apiUrl}/conductor/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId: tidRef.current, text, mode: 'text', channel, projectId: projRef.current || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.threadId) rememberThread(d.threadId);
      if (!r.ok) {
        toast.error(d.error || 'Xatolik');
        setView(v => ({ ...v, items: [...(v.items || []), { id: `err-${Date.now()}`, type: 'system', text: d.error || 'Xatolik', at: new Date().toISOString() }] }));
      }
      if (d.limits) setMe(m => (m ? { ...m, limits: d.limits } : m));
      await loadThread(d.threadId || tidRef.current);
      loadThreads();
    } catch (e) {
      toast.error('Ulanib bo\'lmadi');
    } finally {
      setSending(false); sendingRef.current = false; setLive({ text: '', tools: [], media: [] });
      setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    }
  };

  // ── 📞 Jonli qo'ng'iroq ──
  // turn=true — agent hali o'ylayotgan bo'lsa model so'rovi ham uziladi (yangi gap navbatda kutib qolmasin).
  const cancelServerSpeech = (turn = true) => {
    afRef.current(`${apiUrl}/conductor/voice/cancel`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ turn }) }).catch(() => { });
  };
  const sendCallTurn = async (blob) => {
    const eng = engineRef.current; if (!eng) return;
    callBusyRef.current = true; eng.setBusy(true);
    setSending(true); sendingRef.current = true;
    setLive({ text: '', tools: [], media: [] });
    setView(v => ({ ...v, items: [...(v.items || []), { id: `tmp-v-${Date.now()}`, type: 'user', text: '🎤 …', voicePending: true, at: new Date().toISOString() }] }));
    try {
      const b64 = await blobToBase64(blob);
      const r = await afRef.current(`${apiUrl}/conductor/voice`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId: tidRef.current, audio: b64, speak: true, channel, projectId: projRef.current || undefined, call: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.threadId) rememberThread(d.threadId);
      if (!r.ok) {
        setView(v => ({ ...v, items: (v.items || []).filter(it => !it.voicePending) }));
        if (r.status === 429) { toast.error(d.error || 'Limit tugadi'); endCall(); return; }
        if (r.status !== 400) toast.error(d.error || 'Xatolik');   // 400 = nutq topilmadi — jim o'tamiz
      }
      if (d.limits) setMe(m => (m ? { ...m, limits: d.limits } : m));
      await loadThread(d.threadId || tidRef.current);
    } catch (e) {
      toast.error('Ulanib bo\'lmadi');
    } finally {
      callBusyRef.current = false;
      if (engineRef.current) engineRef.current.setBusy(false);
      setSending(false); sendingRef.current = false; setLive({ text: '', tools: [], media: [] });
    }
  };
  const startCall = async () => {
    if (engineRef.current) return;
    stopAudio();
    const eng = new LiveCallEngine({
      onState: (st) => setCall(c => (c ? { ...c, state: st } : c)),
      onBargeIn: () => cancelServerSpeech(true),
      // ✋ Agent o'ylayotganda gap BOSHLANISHI bilan eski navbat bekor qilinadi — gap tugashini kutmaymiz,
      // shunda yangi gap yuborilganda server allaqachon bo'sh bo'ladi.
      onSpeechStart: ({ barge }) => { if (!barge && callBusyRef.current) cancelServerSpeech(true); },
      onUtterance: (blob) => {
        // Oldingi javob hali tayyorlanayotgan bo'lsa — uning ovozi kerak emas (foydalanuvchi davom etdi).
        if (callBusyRef.current) { cancelServerSpeech(true); if (engineRef.current) engineRef.current.stopPlayback(); }
        callChainRef.current = callChainRef.current.then(() => sendCallTurn(blob)).catch(() => { });
      },
    });
    engineRef.current = eng;
    setCall({ state: 'idle', secs: 0, muted: false });
    try { await eng.start(); }
    catch (e) {
      engineRef.current = null; setCall(null);
      toast.error(micErrorText(e), { duration: 8000 });
      return;
    }
    const t0 = Date.now();
    callTimerRef.current = setInterval(() => setCall(c => (c ? { ...c, secs: Math.floor((Date.now() - t0) / 1000) } : c)), 1000);
  };
  const endCall = () => {
    const eng = engineRef.current; engineRef.current = null;
    if (eng) {
      // ✋ O'lchovlar — bo'lish chegarasini haqiqiy mikrofon ma'lumoti bilan sozlash uchun (fon, xato jim).
      try {
        const st = eng.stats();
        if (st.durMs > 5000) afRef.current(`${apiUrl}/conductor/voice/stats`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ threadId: tidRef.current, source: 'conductor', stats: st }) }).catch(() => { });
      } catch (e) { }
      eng.stop();
    }
    if (callBusyRef.current) cancelServerSpeech(true);
    clearInterval(callTimerRef.current);
    setCall(null);
  };
  useEffect(() => () => { const eng = engineRef.current; engineRef.current = null; if (eng) eng.stop(); clearInterval(callTimerRef.current); }, []);   // eslint-disable-line

  const decide = async (id, action) => {
    setDeciding(id);
    try {
      const r = await afRef.current(`${apiUrl}/conductor/confirmations/${id}/${action}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) toast.error(d.error || 'Bajarib bo\'lmadi');
      else toast.success(action === 'approve' ? 'Bajarildi' : 'Bekor qilindi');
      await loadThread(tidRef.current);
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setDeciding(null); }
  };

  const newThread = () => { rememberThread(null); setView({ items: [], confirmations: [] }); setShowThreads(false); setTimeout(() => inputRef.current && inputRef.current.focus(), 30); };

  // 🗂 B2: har vazifa uchun lentadagi OXIRGI eslatma (tool yoki 📋 forma voqeasi) — karta faqat o'sha joyda.
  const lastCardAt = {};
  for (const it of (view.items || [])) if (it.taskId) lastCardAt[it.taskId] = `${it.type}${it.id}`;
  const cardKey = Object.keys(lastCardAt).join(',');
  cardKeyRef.current = cardKey;
  const loadCards = async () => {
    const ids = cardKeyRef.current;
    if (!ids) return;
    try {
      const r = await afRef.current(`${apiUrl}/task-cards?ids=${ids}`);
      const d = await r.json().catch(() => null);
      if (!r.ok || !Array.isArray(d)) return;
      setCards(c => { const n = { ...c }; for (const id of ids.split(',').map(Number)) n[id] = d.find(x => x.id === id) || { id, missing: true }; return n; });
    } catch (e) { }
  };
  const loadCardsRef = useRef(loadCards); loadCardsRef.current = loadCards;
  useEffect(() => { if (cardKey) loadCardsRef.current(); }, [cardKey]);
  // Faqat tab ko'rinib turganda va sahifa ochiq bo'lsa — 10 s (bitta yengil so'rov, ≤ 30 vazifa).
  useEffect(() => {
    if (!active) return undefined;
    loadCardsRef.current();
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') loadCardsRef.current(); }, 10000);
    return () => clearInterval(t);
  }, [active]);
  // ✅ Tasdiqlash spinneri: approved_at kelguncha yoki 12 daqiqa (merge konflikti / server xatosi).
  useEffect(() => {
    if (!approvingCard) return;
    const t = cards[approvingCard.id];
    if (t && t.approved_at) { setApprovingCard(null); toast.success(`#${approvingCard.id} — deploy qilindi 🚀`); }
    else if (Date.now() - approvingCard.at > 12 * 60 * 1000) { setApprovingCard(null); toast.error('Tasdiqlash yakunlanmadi — 📋 Vazifa tabida log\'ni ko\'ring'); }
  }, [cards, approvingCard]);
  const cardAction = async (id, kind) => {
    const ask = kind === 'stop' ? `Vazifa #${id} to'xtatilsinmi? Bajarilayotgan agent jarayoni o'ldiriladi va o'zgarishlar bekor qilinadi (push/deploy bo'lmaydi).`
      : kind === 'resume' ? `Vazifa #${id} davom ettirilsinmi? Yiqilgan qism vazifalar qayta navbatga qo'yiladi (yarim qilingan ish saqlanadi).`
        : `Vazifa #${id} tasdiqlansinmi?\n\nReview branch (kh/task-${id}) asosiy branchga merge qilinadi va test-muhit qayta deploy qilinadi.`;
    if (!window.confirm(ask)) return;
    if (kind === 'approve') setApprovingCard({ id, at: Date.now() }); else setCardBusy(b => ({ ...b, [id]: true }));
    try {
      const r = await afRef.current(`${apiUrl}/tasks/${id}/${kind}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: kind === 'approve' ? JSON.stringify({ force: false }) : undefined });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error || (kind === 'resume' && !d.ok)) throw new Error(d.error || (r.status === 503 || r.status === 429 ? 'Server hozir band — qayta urinib ko\'ring' : 'Server xatosi'));
      if (kind === 'stop') toast.success(`⏹ To'xtatish so'raldi (vazifa #${id})`);
      else if (kind === 'resume') toast.success(`▶ ${d.resumed || ''} ta qism qayta navbatga qo'yildi`);
      else toast('Merge + redeploy boshlandi…', { icon: '🚀' });
      setTimeout(() => loadCardsRef.current(), 1500);
    } catch (e) {
      toast.error('Xato: ' + (e.message || ''));
      if (kind === 'approve') setApprovingCard(null);
    } finally { if (kind !== 'approve') setCardBusy(b => ({ ...b, [id]: false })); }
  };
  const renderCard = (id) => (
    <TaskLiveCard task={cards[id]} busy={!!cardBusy[id]} approving={!!(approvingCard && approvingCard.id === id)}
      onStop={() => cardAction(id, 'stop')} onResume={() => cardAction(id, 'resume')} onApprove={() => cardAction(id, 'approve')}
      onOpen={() => { if (onOpenTask) onOpenTask(id); }} />
  );

  // Xabarlar va tasdiq kartalari bitta oqimda: karta O'Z tool qatoridan keyin (vaqt soniya
  // aniqligida — bir soniya ichida tartib tasodifiy bo'lardi). Bog'lanmagani vaqt bo'yicha.
  const confs = view.confirmations || [];
  const linked = new Set();
  const timeline = [];
  for (const it of (view.items || [])) {
    timeline.push(it);
    if (it.type === 'tool' && it.confirmationId) {
      const c = confs.find(x => x.id === it.confirmationId);
      if (c) { timeline.push({ ...c, type: 'confirm' }); linked.add(c.id); }
    }
  }
  for (const c of confs) {
    if (linked.has(c.id)) continue;
    const t = ts(c.created_at);
    let i = timeline.length;
    while (i > 0 && ts(timeline[i - 1].at || timeline[i - 1].created_at) > t) i--;
    timeline.splice(i, 0, { ...c, type: 'confirm' });
  }

  const lim = me && me.limits;
  const blocked = lim && lim.blocked;
  const noAccess = me && (!me.access || !me.enabled);

  return (
    <div className={`flex flex-col bg-white dark:bg-gray-800 ${fullScreen || embedded ? 'h-full' : 'h-full rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700'} overflow-hidden`}>
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">🎙 Boshqaruvchi</div>
          {lim && (
            <div className={`text-[11px] ${blocked ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
              title={`Bugungi sarf (OpenRouter). Shaxsiy limit ${fmtUsd(lim.userLimit)}, umumiy ${fmtUsd(lim.globalSpent)} / ${fmtUsd(lim.globalLimit)}. Limitni admin Konfiguratsiyada o'zgartiradi.`}>
              Bugun {fmtUsd(lim.userSpent)} / {fmtUsd(lim.userLimit)}{blocked ? ' — limit tugadi' : ''}
            </div>
          )}
        </div>
        {!(me && me.operator === false) && <button onClick={() => { setShowOv(v => !v); loadOv(); }} title="📡 Navbatchi — topilgan muammolar va o'zi tuzatganlari"
          className={`relative text-xs px-2 py-1 rounded-md border ${showOv ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
          📡{ov && ov.filter(i => i.status === 'open').length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center">{ov.filter(i => i.status === 'open').length}</span>
          )}
        </button>}
        <button onClick={() => (call ? endCall() : startCall())} disabled={!call && (noAccess || blocked || sending)}
          title={call ? 'Qo\'ng\'iroqni tugatish' : '📞 Jonli qo\'ng\'iroq — tugmasiz gaplashamiz: gapiring, men javob beraman; men gapirayotganda gapirsangiz to\'xtayman'}
          className={`text-xs px-2 py-1 rounded-md border disabled:opacity-40 ${call ? 'bg-red-600 border-red-600 text-white' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
          {call ? '📴' : '📞'}
        </button>
        <button onClick={() => (playing ? stopAudio() : setSpeakOn(!speakOn))}
          title={playing ? 'Ovozni to\'xtatish' : speakOn ? 'Javoblar ovozda o\'qiladi (bosib o\'chiring — pul tejaladi)' : 'Javoblar faqat matn (bosib ovozni yoqing)'}
          className={`text-xs px-2 py-1 rounded-md border ${playing ? 'border-emerald-400 text-emerald-600 dark:text-emerald-400 animate-pulse' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'} hover:bg-gray-50 dark:hover:bg-gray-700`}>
          {playing ? '⏹🔊' : speakOn ? '🔊' : '🔇'}
        </button>
        <button onClick={() => { setShowThreads(s => !s); loadThreads(); }} title="Oldingi suhbatlar"
          className="text-xs px-2 py-1 rounded-md border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">🕘</button>
        <button onClick={newThread} title="Yangi suhbat"
          className="text-xs px-2 py-1 rounded-md border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">➕ Yangi</button>
        {onClose && <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none px-1" title="Yopish">✕</button>}
      </div>

      {topSlot}

      {showThreads && (
        <div className="border-b border-gray-100 dark:border-gray-700 max-h-56 overflow-y-auto">
          {threads.length === 0 && <div className="px-4 py-3 text-xs text-gray-400">Hali suhbat yo'q</div>}
          {threads.map(t => (
            <button key={t.id} onClick={() => { rememberThread(t.id); loadThread(t.id); setShowThreads(false); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${t.id === threadId ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}>
              <div className="truncate text-gray-800 dark:text-gray-200" translate="no">{t.title || `Suhbat #${t.id}`}</div>
              <div className="text-[11px] text-gray-400">{new Date(t.updated_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
            </button>
          ))}
        </div>
      )}

      {showOv && (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-gray-900 dark:text-white">📡 Navbatchi</div>
            <button onClick={briefingNow} disabled={ovBusy === 'brief'} title="Kechagi natijalar, muammolar va bugun sizdan nima kerakligi — hozir"
              className="text-xs px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50">{ovBusy === 'brief' ? '⏳' : '☀️ Hisobotni hozir olish'}</button>
          </div>
          <p className="text-[11px] text-gray-400">Har 10 daqiqada tekshiradi. Xavfsiz ma'lum xatolarni o'zi tuzatadi (uzilgan vazifa, ishlamay qolgan test muhiti); merge, prod va byudjet — faqat siz.</p>
          {ov === null && <div className="text-gray-400">⏳</div>}
          {ov && ov.length === 0 && <div className="text-gray-500 dark:text-gray-400 py-4 text-center">✅ Hozircha muammo yo'q</div>}
          {(ov || []).map(i => {
            const sev = i.status === 'fixed' ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20'
              : i.severity === 'high' ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                : i.severity === 'warn' ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700';
            return (
              <div key={i.id} className={`rounded-lg border p-2.5 ${sev}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-gray-500 dark:text-gray-400"><span translate="no">{i.project}</span>{i.status === 'fixed' ? ' · 📡 o\'zim tuzatdim' : ''}</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100 text-[13px]">{i.title}</div>
                    {i.detail && <div className="text-[12px] text-gray-600 dark:text-gray-300 mt-0.5">{i.detail}</div>}
                    {i.action_result && <div className="text-[12px] text-emerald-700 dark:text-emerald-400 mt-0.5">{i.action_result}</div>}
                    {i.suggestion && i.status !== 'fixed' && <div className="text-[12px] text-gray-500 dark:text-gray-400 mt-1">💡 {i.suggestion}</div>}
                  </div>
                </div>
                {i.status === 'open' && (
                  <div className="flex gap-1.5 mt-2">
                    {!!i.fixable && <button disabled={ovBusy === i.id} onClick={() => ovAct(i.id, 'fix')} className="text-xs px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">🔧 Tuzat</button>}
                    <button disabled={ovBusy === i.id} onClick={() => { setShowOv(false); send(`Navbatchi holati: «${i.title}». Batafsil tushuntir va nima qilishni taklif qil.`); }}
                      className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">🎙 So'rash</button>
                    <button disabled={ovBusy === i.id} onClick={() => ovAct(i.id, 'ignore')} className="text-xs px-2 py-1 rounded-md text-gray-400 hover:text-gray-600">🙈 E'tiborsiz</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className={`flex-1 overflow-y-auto px-4 py-3 space-y-2.5 text-sm ${showOv ? 'hidden' : ''}`}>
        {noAccess && (
          <div className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
            {me && !me.enabled ? 'Boshqaruvchi o\'chirilgan.' : 'Sizga Boshqaruvchi hali yoqilmagan.'} Admin → Konfiguratsiya → 🎙 Boshqaruvchi.
          </div>
        )}
        {me && me.access && me.enabled && !me.keyConfigured && (
          <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-3">OpenRouter kaliti sozlanmagan.</div>
        )}
        {timeline.length === 0 && !sending && !noAccess && (
          <div className="py-4">
            {me && me.operator === false ? (
              <p className="text-gray-500 dark:text-gray-400 mb-3">
                Loyihangiz haqida oddiy so'z bilan so'rang yoki yozing. Nima o'zgarganini aytaman va rasmini ko'rsataman,
                yangi ishni qabul qilaman. Narx va muddat bo'yicha savollarni mas'ul odamga yetkazaman — javobi shu yerga keladi.
              </p>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 mb-3">
                Nima qilish kerakligini oddiy so'z bilan yozing. Men holatni tekshiraman, vazifa beraman, rejaga karta qo'shaman,
                konsolni ochaman. Merge, prod va publish kabi qaytarib bo'lmaydigan amallar uchun sizga <b>tugma</b> chiqaraman.
              </p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {(me && me.operator === false ? CLIENT_EXAMPLES : EXAMPLES).map(e => (
                <button key={e} onClick={() => send(e)} className="text-xs px-2.5 py-1.5 rounded-full border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">{e}</button>
              ))}
            </div>
          </div>
        )}
        {timeline.map(it => {
          if (it.type === 'user') return (
            <div key={`u${it.id}`} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary-600 text-white px-3 py-2 whitespace-pre-wrap" translate="no">{it.text}</div>
            </div>
          );
          if (it.type === 'tool') return (
            <div key={`t${it.id}`} className="space-y-1.5">
              <div className={`text-[11px] ${it.ok ? 'text-gray-400' : 'text-red-500'} pl-1`}>{it.ok ? '✓' : '✗'} {it.label}</div>
              {it.draft && (
                // 📝 B3: Boshqaruvchi qoralamasi 📋 Vazifa formasiga tushgan — kerak bo'lsa qaytadan qo'yish.
                <button type="button" onClick={() => { if (uiRef.current) uiRef.current(it.draft); }}
                  title="Qoralamani 📋 Vazifa formasiga qaytadan qo'yish (vazifa hali yaratilmagan — Yuborish'ni siz bosasiz)"
                  className="ml-1 text-[11px] px-2 py-0.5 rounded-md border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30">
                  📋 Formaga qayta qo'yish
                </button>
              )}
              <Media apiUrl={apiUrl} media={it.media} />
              {it.taskId && lastCardAt[it.taskId] === `tool${it.id}` && renderCard(it.taskId)}
            </div>
          );
          if (it.type === 'event') return (
            <div key={`e${it.id}`} className="space-y-1.5">
              <div className="text-[11px] text-gray-400 pl-1">📋 Formadan vazifa berildi{lastCardAt[it.taskId] === `event${it.id}` ? '' : ` — #${it.taskId}`}</div>
              {it.taskId && lastCardAt[it.taskId] === `event${it.id}` && renderCard(it.taskId)}
            </div>
          );
          if (it.type === 'confirm') {
            const st = CONF_ST[it.status] || CONF_ST.pending;
            return (
              <div key={`c${it.id}`} className={`rounded-lg border p-3 ${st.c}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">🔐 {st.t}</div>
                <div className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap text-[13px]">{it.summary}</div>
                {it.status === 'pending' && (
                  <div className="flex gap-2 mt-2.5">
                    <button disabled={deciding === it.id} onClick={() => decide(it.id, 'approve')}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50">{deciding === it.id ? '⏳' : '✅ Bajarish'}</button>
                    <button disabled={deciding === it.id} onClick={() => decide(it.id, 'reject')}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50">✖ Bekor</button>
                  </div>
                )}
                {it.result && it.status === 'failed' && <div className="mt-1.5 text-[12px] text-red-700 dark:text-red-400 break-words">{it.result}</div>}
              </div>
            );
          }
          if (it.type === 'system') return (
            <div key={`s${it.id}`} className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{it.text}</div>
          );
          return (
            <div key={`a${it.id}`} className="flex">
              <div className={`max-w-[92%] rounded-2xl rounded-bl-sm px-3 py-2 text-gray-900 dark:text-gray-100 ${it.escalation ? 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800' : 'bg-gray-100 dark:bg-gray-700'}`}><Rich text={it.text} /></div>
            </div>
          );
        })}
        {sending && (
          <>
            {live.tools.map(t => (
              <div key={t.id} className={`text-[11px] pl-1 ${t.status === 'error' ? 'text-red-500' : 'text-gray-400'}`}>{TOOL_ST[t.status] || '⏳'} {t.label}{t.error ? ` — ${t.error}` : ''}</div>
            ))}
            <Media apiUrl={apiUrl} media={live.media} />
            <div className="flex">
              <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2">
                {live.text ? <Rich text={live.text} /> : <span className="text-gray-400 animate-pulse">● ● ●</span>}
              </div>
            </div>
          </>
        )}
        <div ref={endRef} />
      </div>

      {call ? (
        <div className="border-t border-gray-100 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/40">
          <CallBar state={call.state} engine={engineRef.current} secs={call.secs} muted={call.muted}
            onMute={() => { const m = !call.muted; if (engineRef.current) engineRef.current.setMuted(m); setCall(c => ({ ...c, muted: m })); }}
            onInterrupt={() => { if (engineRef.current) engineRef.current.stopPlayback(); cancelServerSpeech(); }}
            onHangup={endCall} />
          <div className="text-[10px] text-gray-400 mt-1.5">Quloqchin bilan eng yaxshi ishlaydi (dinamikdan qaytgan ovoz meni to'xtatib qo'ymasin). Parol/kalitni aytmang.</div>
        </div>
      ) : (
      <div className="border-t border-gray-100 dark:border-gray-700 p-3">
        <div className="flex items-end gap-2">
          <textarea ref={inputRef} rows={2} value={input} disabled={noAccess || blocked}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
            placeholder={blocked ? 'Bugungi limit tugadi' : (me && me.operator === false ? 'Masalan: saytimda nima o\'zgardi?' : 'Masalan: TezHal da Payme holati qanday?')}
            className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-60" />
          {rec ? (
            <div className="flex items-center gap-1.5">
              <button onClick={() => stopRec(false)} title="Tugatib yuborish"
                className="h-10 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold animate-pulse">⏹ 0:{String(rec.secs).padStart(2, '0')}</button>
              <button onClick={() => stopRec(true)} title="Bekor qilish" className="h-10 px-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 text-sm">✖</button>
            </div>
          ) : input.trim() ? (
            <button onClick={() => send()} disabled={sending || noAccess || blocked}
              className="h-10 px-4 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium disabled:opacity-50">{sending ? '⏳' : 'Yuborish'}</button>
          ) : (
            <button onClick={startRec} disabled={(sending && !voiceBusy) || noAccess || blocked}
              title={voiceBusy ? '✋ Gapini bo\'lib, o\'zingiz gapiring' : '🎤 Gapirish — bosing, gapiring, tugagach yana bosing (60 s gacha)'}
              className="h-10 w-12 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-lg disabled:opacity-50">{voiceBusy ? '✋' : sending ? '⏳' : '🎤'}</button>
          )}
        </div>
        <div className="text-[10px] text-gray-400 mt-1.5">{rec ? '🔴 Yozilmoqda — gapiring, tugagach ⏹ ni bosing' : 'Enter — yuborish · 🎤 — ovoz bilan · 📞 — jonli qo\'ng\'iroq · parol/kalitni aytmang'}</div>
      </div>
      )}
    </div>
  );
}

/** Tugma + oyna. Ruxsat bo'lmasa hech narsa chizilmaydi. */
export default function Conductor({ authFetch, apiUrl, token, variant = 'navbar', onUiAction }) {
  const afRef = useRef(authFetch); afRef.current = authFetch;
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(() => { try { return new URLSearchParams(window.location.search).get('conductor') === '1'; } catch (e) { return false; } });

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const r = await afRef.current(`${apiUrl}/conductor/me`); if (r.ok && alive) setMe(await r.json()); } catch (e) { }
    })();
    return () => { alive = false; };
  }, [apiUrl]);

  if (!me || !me.access || !me.enabled) return null;

  if (variant === 'mobile') {
    return (
      <>
        {!open && (
          <button onClick={() => setOpen(true)} title="🎙 Boshqaruvchi"
            className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-2xl shadow-xl flex items-center justify-center">🎙</button>
        )}
        {open && (
          <div className="dark fixed inset-0 z-50 bg-gray-900"
            style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <ConductorChat authFetch={authFetch} apiUrl={apiUrl} token={token} channel="mobile" onClose={() => setOpen(false)} fullScreen />
          </div>
        )}
      </>
    );
  }
  return (
    <>
      <button onClick={() => setOpen(o => !o)} title="🎙 Boshqaruvchi — Khorezmai'ni suhbat orqali boshqarish"
        className={`h-10 shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg shadow-sm text-sm font-medium transition border
          ${open ? 'bg-primary-700 dark:bg-primary-600 text-white border-primary-700 dark:border-primary-600' : 'bg-white/70 dark:bg-gray-800/70 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800'}`}>
        <span className="text-base leading-none">🎙</span>
      </button>
      {open && (
        // Pastki o'ngda ❓ Yordam tugmasi turadi (z-80) — oyna uning ustida tugaydi, to'smaydi.
        <div className="fixed top-16 right-4 bottom-20 z-50 w-[28rem] max-w-[94vw]">
          <ConductorChat authFetch={authFetch} apiUrl={apiUrl} token={token} channel="web" onClose={() => setOpen(false)} onUiAction={onUiAction} />
        </div>
      )}
    </>
  );
}

/** Admin → Konfiguratsiya kartasi: model, limitlar, kimga yoqilgan. */
export function ConductorAdmin({ authFetch, apiUrl }) {
  const afRef = useRef(authFetch); afRef.current = authFetch;
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [cat, setCat] = useState(null);
  const [sample, setSample] = useState("Assalomu alaykum! Men Khorezmai Boshqaruvchisiman. TezHal loyihasida bugun uchta vazifa tugadi, bittasi sizning tasdig'ingizni kutmoqda.");
  const [preview, setPreview] = useState(null);   // {busy} | {seconds, cost, voice, model} | {error}
  const [ovs, setOvs] = useState(null);           // 📡 Navbatchi sozlamalari
  const [ovRun, setOvRun] = useState(null);
  const audioRef = useRef(null);

  const loadCatalog = async () => {
    try { const r = await afRef.current(`${apiUrl}/conductor/admin/catalog`); if (r.ok) setCat(await r.json()); } catch (e) { }
  };
  const listen = async () => {
    setPreview({ busy: true });
    try {
      const r = await afRef.current(`${apiUrl}/conductor/admin/tts-preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: sample, model: form.conductor_tts_model, voice: form.conductor_tts_voice }),
      });
      const d = await r.json();
      if (!r.ok) { setPreview({ error: d.error || 'Xato' }); return; }
      try { if (audioRef.current) audioRef.current.pause(); } catch (e) { }
      const a = new Audio(`data:audio/mpeg;base64,${d.audio}`);
      audioRef.current = a; a.play().catch(() => { });
      setPreview({ seconds: d.seconds, cost: d.cost, voice: d.voice, model: d.model });
    } catch (e) { setPreview({ error: 'Ulanib bo\'lmadi' }); }
  };
  useEffect(() => () => { try { if (audioRef.current) audioRef.current.pause(); } catch (e) { } }, []);

  const load = async () => {
    try {
      const r = await afRef.current(`${apiUrl}/conductor/admin`);
      const d = await r.json();
      if (!r.ok) { setErr(d.error || 'Xato'); return; }
      setData(d); setForm(d.settings); setErr(null);
    } catch (e) { setErr('Yuklab bo\'lmadi'); }
  };
  const loadOvs = async () => { try { const r = await afRef.current(`${apiUrl}/overseer/settings`); if (r.ok) setOvs(await r.json()); } catch (e) { } };
  const saveOvs = async (patch) => {
    try {
      const r = await afRef.current(`${apiUrl}/overseer/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      const d = await r.json(); if (!r.ok) { toast.error(d.error || 'Saqlanmadi'); return; } setOvs(d); toast.success('Saqlandi');
    } catch (e) { toast.error('Saqlanmadi'); }
  };
  const runOv = async () => {
    setOvRun({ busy: true });
    try { const r = await afRef.current(`${apiUrl}/overseer/run`, { method: 'POST' }); setOvRun(await r.json()); } catch (e) { setOvRun({ error: 'Ulanib bo\'lmadi' }); }
  };
  useEffect(() => { load(); loadCatalog(); loadOvs(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await afRef.current(`${apiUrl}/conductor/admin/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi'); return; }
      setData(d); setForm(d.settings); toast.success('Saqlandi');
    } catch (e) { toast.error('Saqlanmadi'); } finally { setSaving(false); }
  };
  const setUser = async (id, patch) => {
    try {
      const r = await afRef.current(`${apiUrl}/conductor/admin/users/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi'); return; }
      setData(d);
      if (patch.operator !== undefined) setForm(f => ({ ...f, conductor_operator_ids: (d.settings || {}).conductor_operator_ids }));
    } catch (e) { toast.error('Saqlanmadi'); }
  };
  // 👥 F5: loyiha kunlik chegarasi va muomala darajasini qayta baholash.
  const setProject = async (pid, dailyUsd) => {
    try {
      const r = await afRef.current(`${apiUrl}/conductor/admin/projects/${pid}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dailyUsd }) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi'); return; }
      setData(d); toast.success('Saqlandi');
    } catch (e) { toast.error('Saqlanmadi'); }
  };
  const [assessing, setAssessing] = useState(null);
  const assess = async (id) => {
    setAssessing(id);
    try {
      const r = await afRef.current(`${apiUrl}/conductor/admin/users/${id}/assess`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || 'Baholab bo\'lmadi'); return; }
      if (d.overview) setData(d.overview);
      toast.success(`Baho: ${d.level} — ${d.reason || ''}`);
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setAssessing(null); }
  };

  const inp = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-2.5 py-1.5';
  // ATAYIN oddiy funksiya (komponent EMAS): ichki komponent har render'da qayta mount bo'lib
  // yozayotganda fokus yo'qolardi.
  const priceOf = (id) => {
    const m = cat && (cat.chat || []).find(x => x.id === id);
    return m ? `$${m.in} / $${m.out} har 1M token (kirish/chiqish)${m.audio_in ? ` · audio $${m.audio_in}/1M` : ''}` : (cat ? 'narx topilmadi — model nomini tekshiring' : '');
  };
  const field = (k, label, hint, type = 'text', list) => (
    <label key={k} className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{label}</span>
      <input type={type} step={type === 'number' ? '0.1' : undefined} list={list} className={inp} value={form[k] ?? ''} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} />
      {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
    </label>
  );

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700 mt-6">
      <div className="flex items-center justify-between mb-2 border-b pb-2">
        <h2 className="text-xl font-semibold">🎙 Boshqaruvchi</h2>
        {data && <span className="text-xs text-gray-400">Bugun {fmtUsd(data.spentToday)} · 30 kun {fmtUsd(data.spent30d)} · OpenRouter {data.keyConfigured ? '✓ kalit bor' : '✗ kalit yo\'q'}</span>}
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Suhbat orqali Khorezmai'ni boshqaradigan agent. Model <b>OpenRouter</b> orqali, <b>pullik</b>: Claude Max'dan emas.
        Kod yozish baribir engine'da (Max) qoladi. <b>Matn</b> uchun sifatli model, <b>ovoz</b> uchun tez model.
        Asosiy model yiqilsa zaxira model ishlaydi. Kunlik limit tugasa model chaqirilmaydi.
        Qaytarib bo'lmaydigan amallar (merge, prod, publish, o'chirish) faqat foydalanuvchi tugmani bosganda bajariladi.
      </p>
      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
      {form && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm md:col-span-3">
              <input type="checkbox" checked={form.conductor_enabled === '1'} onChange={(e) => setForm(f => ({ ...f, conductor_enabled: e.target.checked ? '1' : '0' }))} />
              Boshqaruvchi yoqilgan
            </label>
            {field('conductor_model_text', "Model — matnli suhbat", priceOf(form.conductor_model_text) || "masalan anthropic/claude-sonnet-5", 'text', 'kh-cond-models')}
            {field('conductor_model_voice', "Model — ovozli suhbat", priceOf(form.conductor_model_voice) || "tez: anthropic/claude-haiku-4.5", 'text', 'kh-cond-models')}
            {field('conductor_model_fallback', "Zaxira model", priceOf(form.conductor_model_fallback) || "asosiysi yiqilsa", 'text', 'kh-cond-models')}
            {field('conductor_stt_model', "Nutqni tanish (ovozni matnga)", priceOf(form.conductor_stt_model) || "google/gemini-3.8-flash", 'text', 'kh-cond-models')}
            <datalist id="kh-cond-models">
              {(cat ? cat.chat : []).map(m => <option key={m.id} value={m.id}>{`$${m.in}/$${m.out}`}</option>)}
            </datalist>
            {field('conductor_daily_usd_global', "Kunlik umumiy limit ($)", "hamma foydalanuvchilar birga", 'number')}
            {field('conductor_daily_usd_user', "Kunlik limit — har user default ($)", "pastda har kimga alohida", 'number')}
            {field('conductor_operator_title', "Mijozga operator nomi", "«… bilan kelishib javob beraman» — masalan Adham aka")}
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-3">
            <div className="text-sm font-semibold mb-2">🔊 Ovoz (Boshqaruvchi gapirganda)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Ovoz modeli</span>
                <select className={inp} value={form.conductor_tts_model || ''} onChange={(e) => setForm(f => ({ ...f, conductor_tts_model: e.target.value }))}>
                  {!(cat && cat.tts.some(m => m.id === form.conductor_tts_model)) && <option value={form.conductor_tts_model}>{form.conductor_tts_model}</option>}
                  {(cat ? cat.tts : []).map(m => <option key={m.id} value={m.id}>{m.id.replace('google/gemini-', '')} · ${m.per_min.toFixed(4)}/daq</option>)}
                </select>
                <span className="text-[11px] text-gray-400">narx — gapirilgan har daqiqa uchun (taxminiy, OpenRouter narxidan)</span>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Ovoz</span>
                <select className={inp} value={form.conductor_tts_voice || ''} onChange={(e) => setForm(f => ({ ...f, conductor_tts_voice: e.target.value }))}>
                  {(cat ? cat.voices : [{ id: form.conductor_tts_voice, style: '' }]).map(v => <option key={v.id} value={v.id}>{v.id}{v.style ? ` — ${v.style}` : ''}</option>)}
                </select>
                <span className="text-[11px] text-gray-400">narx ovozga bog'liq emas — faqat modelga</span>
              </label>
              <div className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Namuna</span>
                <div className="flex gap-2">
                  <input className={inp} value={sample} onChange={(e) => setSample(e.target.value)} />
                  <button onClick={listen} disabled={preview && preview.busy} title="Tanlangan model va ovozda eshitish"
                    className="shrink-0 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50">{preview && preview.busy ? '⏳' : '▶ Eshitish'}</button>
                </div>
                <span className={`text-[11px] ${preview && preview.error ? 'text-red-500' : 'text-gray-400'}`}>
                  {preview && preview.error ? preview.error
                    : preview && preview.seconds ? `${preview.voice}: ${preview.seconds} s · ~$${Number(preview.cost || 0).toFixed(4)} · 1 daqiqa ≈ $${(preview.seconds ? preview.cost / preview.seconds * 60 : 0).toFixed(4)}`
                      : 'eshitish ~$0.002 turadi'}
                </span>
              </div>
            </div>
          </div>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium disabled:opacity-50">{saving ? '⏳' : 'Saqlash'}</button>
        </>
      )}
      {ovs && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 mt-4">
          <div className="text-sm font-semibold mb-1">📡 Navbatchi</div>
          <p className="text-[11px] text-gray-400 mb-2">Har 10 daqiqada yiqilgan / qotgan ishlarni, deploy va servislarni kuzatadi; ertalab hisobot yuboradi (Telegram matn + ovoz, 🎙 suhbat).</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={ovs.overseer_enabled === '1'} onChange={(e) => saveOvs({ overseer_enabled: e.target.checked ? '1' : '0' })} /> Yoqilgan
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Mustaqillik</span>
              <select className={inp} value={ovs.overseer_autonomy} onChange={(e) => saveOvs({ overseer_autonomy: e.target.value })}>
                <option value="observe">kuzatadi (faqat hisobot)</option>
                <option value="suggest">taklif qiladi (tuzatishni siz bosasiz)</option>
                <option value="safe">xavfsizini o'zi qiladi</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Hisobot soati (Toshkent)</span>
              <select className={inp} value={ovs.overseer_briefing_hour} onChange={(e) => saveOvs({ overseer_briefing_hour: e.target.value })}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={String(h)}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={ovs.overseer_briefing_voice === '1'} onChange={(e) => saveOvs({ overseer_briefing_voice: e.target.checked ? '1' : '0' })} /> Hisobot ovozda ham
            </label>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={runOv} disabled={ovRun && ovRun.busy} className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600">{ovRun && ovRun.busy ? '⏳' : '▶ Hozir tekshirish'}</button>
            {ovRun && !ovRun.busy && <span className="text-[11px] text-gray-500">{ovRun.error || `topildi ${ovRun.found ?? 0} · yangi ${ovRun.fresh ?? 0} · tuzatildi ${ovRun.fixed ?? 0} · hal bo'ldi ${ovRun.resolved ?? 0}${ovRun.seeding ? ' (birinchi marta — faqat qayd)' : ''}`}</span>}
          </div>
        </div>
      )}
      {data && (
        <div className="mt-5 overflow-x-auto">
          <div className="text-sm font-semibold mb-1">Foydalanuvchilar</div>
          <p className="text-[11px] text-gray-400 mb-2">
            <b>Operator</b> — studiya egasi: mijoz vazifalariga ruxsat beradi, mijozning narx/muddat savollari unga keladi (belgilanmasa — adminlar).
            <b> Vazifasi ruxsatdan o'tadi</b> — bu odam bergan vazifa (ilova, telefon, 🎙) operator yoki loyiha egasi ✅ bermaguncha boshlanmaydi.
            <b> Muomala</b> — «avto»da Boshqaruvchi suhbatdan o'zi baholaydi; oddiy = jargonsiz, rasm bilan.
          </p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="py-1.5 pr-2">User</th><th className="pr-2">Yoqilgan</th><th className="pr-2">Operator</th><th className="pr-2">Vazifasi ruxsatdan o'tadi</th><th className="pr-2">Kunlik limit ($)</th><th className="pr-2">Muomala</th><th className="pr-2">Bugun</th>
            </tr></thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.id} className="border-b border-gray-50 dark:border-gray-700/50">
                  <td className="py-1.5 pr-2">
                    <span translate="no">{u.username}</span>{u.role === 'admin' && <span className="ml-1 text-[10px] text-gray-400">admin</span>}{!u.is_active && <span className="ml-1 text-[10px] text-red-400">o'chiq</span>}
                    {u.projects && <div className="text-[10px] text-gray-400 max-w-[14rem] truncate" title={u.projects} translate="no">{u.projects}</div>}
                  </td>
                  <td className="pr-2">{u.role === 'admin' ? <span className="text-xs text-gray-400">doim</span> : (
                    <input type="checkbox" checked={u.conductor_enabled} onChange={(e) => setUser(u.id, { enabled: e.target.checked })} />)}</td>
                  <td className="pr-2">
                    <input type="checkbox" checked={!!u.is_operator} title={u.role === 'admin' && !(data.operatorIds || []).includes(u.id) ? 'Operator ro\'yxati bo\'sh — adminlar operator hisoblanadi' : ''}
                      onChange={(e) => setUser(u.id, { operator: e.target.checked })} />
                  </td>
                  <td className="pr-2">{(u.role === 'admin' || u.is_operator) ? <span className="text-xs text-gray-400">kerak emas</span> : (
                    <input type="checkbox" checked={!!u.task_needs_approval} onChange={(e) => setUser(u.id, { taskNeedsApproval: e.target.checked })} />)}</td>
                  <td className="pr-2">
                    <input key={`${u.id}-${u.conductor_daily_usd}`} type="number" step="0.1" min="0" defaultValue={u.conductor_daily_usd ?? ''} placeholder={`default ${form ? form.conductor_daily_usd_user : ''}`}
                      onBlur={(e) => { const v = e.target.value; if (String(v) !== String(u.conductor_daily_usd ?? '')) setUser(u.id, { dailyUsd: v === '' ? null : v }); }}
                      className="w-28 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm px-2 py-1" />
                  </td>
                  <td className="pr-2">
                    <div className="flex items-center gap-1">
                      <select value={u.tech_level || ''} onChange={(e) => setUser(u.id, { techLevel: e.target.value || null })}
                        title={u.tech_level_reason || ''}
                        className="rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm px-2 py-1">
                        <option value="">{u.is_operator ? 'avto (operator — texnik)' : `avto${u.tech_level_guess ? ` (${u.tech_level_guess})` : ' (hali baholanmagan)'}`}</option>
                        <option value="texnik">texnik</option>
                        <option value="oddiy">oddiy (IT'ni bilmaydi)</option>
                      </select>
                      {!u.tech_level && !u.is_operator && (
                        <button onClick={() => assess(u.id)} disabled={assessing === u.id} title="Suhbatlaridan hozir qayta baholash (~$0.001)"
                          className="text-xs px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50">{assessing === u.id ? '⏳' : '🔄'}</button>
                      )}
                    </div>
                  </td>
                  <td className="pr-2 font-mono text-xs">{fmtUsd(u.spent_today)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {Array.isArray(data.projects) && data.projects.length > 0 && (
            <>
              <div className="text-sm font-semibold mt-5 mb-1">Loyihalar — kunlik chegara</div>
              <p className="text-[11px] text-gray-400 mb-2">Boshqaruvchi sarfi suhbat qaysi loyiha haqida bo'lsa o'shanga yoziladi. Chegara tugasa shu loyiha bo'yicha suhbat ertagacha to'xtaydi. Bo'sh — cheklanmagan.</p>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-1.5 pr-2">Loyiha</th><th className="pr-2">Bugun</th><th className="pr-2">30 kun</th><th className="pr-2">Kunlik chegara ($)</th>
                </tr></thead>
                <tbody>
                  {data.projects.map(p => (
                    <tr key={p.id} className="border-b border-gray-50 dark:border-gray-700/50">
                      <td className="py-1.5 pr-2" translate="no">{p.name}</td>
                      <td className="pr-2 font-mono text-xs">{fmtUsd(p.spent_today)}</td>
                      <td className="pr-2 font-mono text-xs">{fmtUsd(p.spent_30d)}</td>
                      <td className="pr-2">
                        <input key={`p${p.id}-${p.daily_usd}`} type="number" step="0.1" min="0" defaultValue={p.daily_usd ?? ''} placeholder="cheklanmagan"
                          onBlur={(e) => { const v = e.target.value; if (String(v) !== String(p.daily_usd ?? '')) setProject(p.id, v === '' ? null : v); }}
                          className="w-28 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm px-2 py-1" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
